class VehicleSearchFlex {
  constructor(parent) {
    this.parent = parent;

    this.elements = {
      container: document.querySelector('#js-free-text-search'),
    };

    this.handleClick = this.handleClick.bind(this);
    this.init();
  }

  init() {
    this.elements.container.addEventListener('click', this.handleClick);
    this.#changeFlexSearchPosition();
  }

  /**
   * Click handler for links in the dropdown list
   * @param {Event} { target }
   */
  handleClick({ target }) {
    if (target?.dataset?.raw) {
      let raw = {};
      try {
        raw = JSON.parse(target.dataset.raw);
      } catch (e) {
        console.log(e);
      }
      // turn raw parameters into filter selections
      const allParams = Object.entries(raw)
        .filter(([field]) => !['language', 'slugs', 'params'].includes(field))
        .map(([field, value]) => ({ field, value }));
      if (raw?.slugs) {
        const slugs = raw.slugs.split('/');
        if (slugs[0]) allParams.push({ field: 'make[]', value: slugs[0] });
        if (slugs[1]) allParams.push({ field: 'model[]', value: slugs[1] });
      }

      if (raw?.params) {
        const params = new URLSearchParams(raw?.params);
        for (const [key, value] of params.entries()) {
          allParams.push({ field: key, value: value });
        }
      }

      this.#handleFields(allParams);

      this.elements.container.classList.remove('show-suggestions');
    }
  }

  /**
   * translates param names into the hybrid search fields which are named differently
   * @param {string} field name of param to translate
   * @returns {string} translated field name
   */
  #mapFieldname(field) {
    const fieldMapping = {
      make: 'make[]',
      model: 'model[]',

      'fueltype[]': 'fuel[]',
      fueltype: 'fuel[]',

      'bodytype[]': 'body_type[]',
      bodytype: 'body_type[]',

      'drivetype[]': 'drive[]',
      drivetype: 'drive[]',

      category: 'search_type[]',
    };
    return fieldMapping[field] ?? field;
  }

  /**
   * Handles an array of parameters to selected filters
   * @param {object[]} params array of {field, value} pairs
   */
  #handleFields(params) {
    for (let { field, value } of params) {
      const selector = `[name="${this.#mapFieldname(field)}"]`;
      const element = document.querySelector(selector);
      switch (element?.type) {
        case 'select-multiple':
        case 'select-one':
          this.#handleSelect(field, value, element);
          break;
        case 'radio':
        case 'checkbox':
          this.#handleCheckable(selector, field, value);
          break;
        case 'number':
          this.#handleNumber(field, value, element);
          break;
        default:
          console.log('unhandled field type', element?.type, element, field, value, selector);
      }
    }
  }

  /**
   * Selects an option in a select
   * @param {string} _field name of select field to be modified
   * @param {string} value value to select
   * @param {HTMLElement} element handle to the select HTML element
   */
  #handleSelect(_field, value, element) {
    const findOption = [...element.options].find(option => option.value.localeCompare(value, undefined, { sensitivity: 'base' }) === 0);
    if (findOption) {
      element.options[0].selected = false;
      element.options[0].toggleAttribute('selected', false);
      findOption.selected = true;
      element.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Sets value to group of checkables
   * @param {string} fieldSelector query selector pointing to the inputs
   * @param {string} field name of checkbox or radio field to be modified
   * @param {string} value value to select
   */
  #handleCheckable(fieldSelector, field, value) {
    const unSelector = `${fieldSelector}:checked`;
    document.querySelector(unSelector)?.toggleAttribute('checked', false);
    const selector = `${fieldSelector}[value="${value}"]`;
    document.querySelector(selector)?.toggleAttribute('checked', true);
  }

  /**
   * Sets value to number field - unsure if this is really used? There didn't seem to be numeric values in the prepoluated searches
   * @param {string} field name of input field to be modified
   * @param {string} value value to set
   * @param {HTMLElement} element input field to be modified
   */
  #handleNumber(field, value, element) {
    element.value = value;
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Changes the position of the flex search input based on scroll position
   * We need this to ensure input is clickable
   */
  #changeFlexSearchPosition() {
    // get mobile breakpoint from CSS variable default is 600px
    const mobileBreakPoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-sm'), 10) || 1200;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    // if viewport is smaller than mobile breakpoint
    if (viewportWidth <= mobileBreakPoint) {
      // check scroll position is top of the page
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      // small margin and we want that te input is visible when scrolled
      const padding = 12;
      if (scrollTop <= padding) {
        // add z-index 71 to container why 71?
        // because rajaa hakua has z-index 70, need to be above it
        this.elements.container.style.zIndex = '71';
        this.elements.container.classList.remove('force-hide-suggestions');
      } else {
        // Set z-index to 3. This allows the input goes under the nav but is high enough
        // for the suggestion box in wider resolutions to be placed at the top
        this.elements.container.style.zIndex = '3';
        // force-hide-suggestions class ensures that an open search suggestion box is not shown
        // when the page is scrolled down and the filter button (which is below the suggestion box)
        // is supposed to cover the top menu.
        this.elements.container.classList.add('force-hide-suggestions');
        // If we have the suggestion container open, we also need to close it
      }
    }

    // add scroll listener to toggle visibility
    window.addEventListener('scroll', () => {
      // add small debounce/timeout to avoid too many calls
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.#changeFlexSearchPosition();
      }, 25);
    });
    
    // add resize listener to ensure visibility works on resize
    window.addEventListener('resize', () => {
      const mobileBreakPoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-sm'), 10) || 1200;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (viewportWidth <= mobileBreakPoint) {
      // add small debounce/timeout to avoid too many calls
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
         this.#changeFlexSearchPosition();
        }, 25);
      }
      else {
        // if viewport is larger than mobile breakpoint, reset z-index
        this.elements.container.style.zIndex = '3';
        this.elements.container.classList.remove('force-hide-suggestions');
      }
    });
  }
}

export { VehicleSearchFlex };
